/* AdminToolRequests — admin queue for teacher-submitted tool/simulation requests.
 * Lists all requests, groups by status, lets admin advance status and add notes.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Check, CircleDot, Sparkles, X, Trash2, Search } from "lucide-react";
import api from "@/lib/api";

const STATUSES = [
  { key: "open",         label: "Open",       color: "#F59E0B" },
  { key: "in_progress",  label: "In progress",color: "#0EA5E9" },
  { key: "shipped",      label: "Shipped",    color: "#10B981" },
  { key: "rejected",     label: "Rejected",   color: "#6B7280" },
];

export default function AdminToolRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tool-requests");
      setRequests(data.requests || []);
    } catch { toast.error("Could not load requests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (selected) setNotes(selected.admin_notes || ""); }, [selected]);

  const filtered = useMemo(() => {
    const qs = q.trim().toLowerCase();
    return requests.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!qs) return true;
      return (r.tool_name || "").toLowerCase().includes(qs)
        || (r.subject || "").toLowerCase().includes(qs)
        || (r.teacher_name || "").toLowerCase().includes(qs);
    });
  }, [requests, q, filter]);

  const counts = useMemo(() => {
    const c = { open: 0, in_progress: 0, shipped: 0, rejected: 0 };
    for (const r of requests) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [requests]);

  const update = async (status, extra = {}) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/tool-requests/${selected.id}`, { status, admin_notes: notes, ...extra });
      toast.success(`Marked ${status.replace("_", " ")}`);
      await load();
      setSelected((s) => ({ ...s, status, admin_notes: notes }));
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0C10] text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={14} className="text-[#FFB400]" />
          <span className="text-[11px] font-mono uppercase tracking-[0.28em] text-white/50">Admin · Tool requests</span>
        </div>
        <h1 className="text-3xl font-display font-bold mb-6">Teacher requests · review queue</h1>

        {/* Status pills */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <button onClick={() => setFilter("all")} data-testid="req-filter-all"
            className={`text-xs px-3 py-1.5 rounded-md border ${filter === "all" ? "bg-white text-[#0A0C10] border-white" : "border-white/20 hover:border-white"}`}>
            All · {requests.length}
          </button>
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`req-filter-${s.key}`}
              className={`text-xs px-3 py-1.5 rounded-md border inline-flex items-center gap-2 ${filter === s.key ? "border-white bg-white/10" : "border-white/20 hover:border-white/60"}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              {s.label} · {counts[s.key] || 0}
            </button>
          ))}
          <div className="flex-1" />
          <div className="inline-flex items-center gap-2 border border-white/20 rounded-md px-3 py-1.5">
            <Search size={12} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="req-search"
              className="bg-transparent outline-none text-xs text-white placeholder:text-white/40 w-48" />
          </div>
        </div>

        {/* Table */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-2">
            {loading && <div className="text-center py-16 text-white/40 inline-flex items-center gap-2 justify-center w-full"><Loader2 size={14} className="animate-spin" /> Loading requests…</div>}
            {!loading && filtered.length === 0 && <div className="text-center py-16 text-white/40 text-sm">No requests match your filter.</div>}
            {filtered.map((r) => {
              const st = STATUSES.find((s) => s.key === r.status) || STATUSES[0];
              return (
                <motion.button key={r.id} onClick={() => setSelected(r)} data-testid={`req-item-${r.id}`}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className={`w-full text-left p-4 rounded-lg border ${selected?.id === r.id ? "border-[#FFB400] bg-white/5" : "border-white/10 hover:border-white/40 hover:bg-white/[.03]"} transition-colors`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">{r.tool_name}</div>
                      <div className="text-[11px] font-mono uppercase tracking-wider text-white/50 mt-1">
                        {r.kind} · {r.subject || "no subject"} · by {r.teacher_name}
                      </div>
                      <div className="text-xs text-white/70 mt-2 line-clamp-2">{r.description}</div>
                    </div>
                    <div className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded" style={{ background: st.color + "20", color: st.color }}>
                      <CircleDot size={10} /> {st.label}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-6 h-fit">
            {!selected && (
              <div className="border border-dashed border-white/20 rounded-lg p-8 text-center text-white/40 text-xs">
                Pick a request on the left to review, add notes, and change status.
              </div>
            )}
            {selected && (
              <div className="border border-white/10 rounded-lg p-5 bg-white/[.02]" data-testid="req-detail-panel">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-white/50">{selected.kind} · {selected.subject || "no subject"}</div>
                    <div className="text-lg font-bold text-white mt-1">{selected.tool_name}</div>
                  </div>
                  <button onClick={() => setSelected(null)} data-testid="req-detail-close" className="text-white/50 hover:text-white"><X size={14} /></button>
                </div>
                <div className="text-[11px] text-white/60 mb-3">
                  by <span className="font-semibold text-white">{selected.teacher_name}</span> · <span className="font-mono">{selected.teacher_email}</span>
                </div>
                <div className="text-sm text-white/80 whitespace-pre-wrap p-3 border border-white/10 rounded-md bg-black/30 mb-4">{selected.description}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-1">Admin notes</div>
                <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="req-detail-notes"
                  placeholder="Internal notes for this request…"
                  className="w-full text-sm bg-black/30 border border-white/10 rounded-md p-2 outline-none focus:border-white/40 text-white placeholder:text-white/30" />
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {STATUSES.map((s) => (
                    <button key={s.key} onClick={() => update(s.key)} disabled={saving || selected.status === s.key}
                      data-testid={`req-status-${s.key}`}
                      className="text-xs px-3 py-2 rounded-md border border-white/20 hover:border-white inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: selected.status === s.key ? s.color + "20" : "transparent", color: selected.status === s.key ? s.color : undefined }}
                    >
                      {selected.status === s.key ? <Check size={11} /> : <CircleDot size={11} />} {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
